// main.js

import '../scss/app.scss';

import { AuthManager } from './lib/http/AuthManager.js';
import { App } from './app/App.js';
import { routes, defaultRoutes } from './app/routes.js';
import { generatedRoutes } from './app/routes-generated.js';
import { menu } from './app/main-menu.js';
import { generatedMenuItems } from './app/main-menu-generated.js';
import { loadConfig } from './config.js';
import { i18n } from './lib/globalization/i18n.js';
import { default as moment } from "moment";
import { default as momenttz } from "moment-timezone";
import { AppStorage } from './app/data/AppStorage.js';
import { Logger } from './lib/tracing/Logger.js';
import { HttpService } from './lib/data/services/HttpService.js';
import { ServiceFactory } from './lib/data/services/ServiceFactory.js';
import { WorkspaceManager } from './app/modules/projects/managers/WorkspaceManager.js';
import { TunnelDesignApplicationServices } from './app/TunnelDesignApplicationServices.js';
import { DomVmElementRenderer } from './lib/ui/rendering/DomVmElementRenderer.js';
import { FontawesomeIconRenderer } from './lib/ui/rendering/fontawesome-icons/FontawesomeIconRenderer.js';
import { HomeBrewedRenderer } from './lib/ui/rendering/homebrewed-css/HomeBrewedRenderer.js';
import { preprocessGeneratedMenuItems, preprocessGeneratedRoutes, preprocessManualMenuItems } from './app/generated-artifacts-preprocessor.js';

const localeResourceModules = import.meta.glob(
    [
        './app/resources/locales/**/*.js',
        './lib/resources/locales/**/*.js',
    ],
    { eager: false }
);

async function startApp() {

    const config = await loadConfig('/config/config.json');

    i18n.setLocaleResourceModules(localeResourceModules);
    await i18n.setLocale(config.locale, true);

    const storage = new AppStorage();
    const logger = new Logger();
    const authHttpService = new HttpService({
        baseUrl: config.apiBaseUrl,
        defaultAuth: { type: 'cookie' },
        defaultContentType: 'json',
        defaultResponseType: 'json',
    });
    const authServiceFactory = new ServiceFactory({
        httpClient: authHttpService,
        storage,
    });
    const authService = authServiceFactory.createAuthService();
    const authManager = new AuthManager({
        authService,
        authMethod: 'cookie',
        storage: window.localStorage,
        storageKey: 'inis-auth',
    });
    const httpService = new HttpService({
        baseUrl: config.apiBaseUrl,
        defaultContentType: 'json',
        defaultResponseType: 'json',
        authManager,
    });
    const serviceFactory = new ServiceFactory({
        httpClient: httpService,
        storage,
    });
    const workspaceManager = new WorkspaceManager({
        httpClient: httpService,
    });
    const elementRenderer = new DomVmElementRenderer();
    const iconRenderer = new FontawesomeIconRenderer({ elementRenderer });
    const renderer = new HomeBrewedRenderer({ elementRenderer, iconRenderer });

    const processedGeneratedRoutes = preprocessGeneratedRoutes(generatedRoutes);
    const appRoutes = processedGeneratedRoutes.concat(routes);

    const processedManualMenuItems = preprocessManualMenuItems(menu, routes);
    const processedGeneratedMenuItems = preprocessGeneratedMenuItems(generatedMenuItems, appRoutes);
    const mainMenuDefs = processedManualMenuItems.concat(processedGeneratedMenuItems);

    const services = new TunnelDesignApplicationServices({
        config,
        i18n,
        authService,
        authManager,
        httpService,
        serviceFactory,
        storage,
        logger,
        renderer,
        workspaceManager,
    });

    const app = new App({
        services,
        routes: appRoutes,
        defaultRoutes,
        mainMenuDefs,
    });

    // -------------------- moment initialization

    moment.locale(config.locale);
    moment.tz.guess();
    moment.tz('Europe/Prague');

    // -------------------- app start

    app.start(document.getElementById('app'), { consumeRootElement: true });

}

startApp();
